/**
 * /my-coaching/business/marketing-suite — the Marketing Suite page: the sales
 * page, or "Your plan" for a coach who holds only the Suite. The menu sends a
 * coach here unless she holds the Suite beside a NOVA plan. A revoked coach
 * goes to the dashboard.
 */

import { createFileRoute, redirect } from "@tanstack/react-router";
import { z } from "zod";

import { publicPlanPricingOptions } from "~/lib/functions/plan-pricing";
import { callsAndLinksQueryOptions } from "~/lib/queries/callsAndLinksQuery";
import { placementVideosQueryOptions } from "~/lib/queries/learningQuery";
import { suitePlanOfferQueryOptions } from "~/lib/queries/suitePlanOfferQuery";
import { holdsSuiteOnly } from "~/lib/shared/coach-account";
import { BILLING_TERMS, type BillingTerm } from "~/lib/shared/plan-catalogue";
import { DEFAULT_SUITE_TERM } from "~/lib/shared/suite-choice";

import { MarketingSuitePage } from "./-components/marketing-suite-page";

export const Route = createFileRoute(
  "/_user/my-coaching/business/marketing-suite",
)({
  staticData: { title: "Marketing Suite" },
  validateSearch: z.object({
    /** The billing period the choice block shows. It stays in the address so
     *  coming back from Plans and Pricing shows the same period. A value that
     *  is not a period is dropped, never an error page. */
    term: z.enum(BILLING_TERMS).optional().catch(undefined),
  }),
  beforeLoad: ({ context }) => {
    if (context.isRevoked) {
      throw redirect({ to: "/my-coaching/clients/dashboard" });
    }
  },
  loader: async ({ context }) => {
    // A missing video leaves its tab out, prices that cannot be read leave a
    // line about Plans and Pricing (or, on the Your plan page, leave out the
    // price sentences), and a missing walkthrough link leaves the banner out;
    // none is an error page.
    await Promise.all([
      context.queryClient
        .ensureQueryData(placementVideosQueryOptions())
        .catch(() => undefined),
      (context.account && holdsSuiteOnly(context.account)
        ? context.queryClient.ensureQueryData(suitePlanOfferQueryOptions())
        : context.queryClient.ensureQueryData(publicPlanPricingOptions)
      ).catch(() => undefined),
      // Only the sales page shows the banner; a Suite holder never reaches it.
      context.account?.hasSuite
        ? undefined
        : context.queryClient
            .ensureQueryData(callsAndLinksQueryOptions())
            .catch(() => undefined),
    ]);
  },
  component: MarketingSuiteRoute,
});

function MarketingSuiteRoute(): React.JSX.Element {
  const account = Route.useRouteContext({
    select: (context) => context.account,
  });
  const { term = DEFAULT_SUITE_TERM } = Route.useSearch();
  const navigate = Route.useNavigate();
  // The switch sits in the plan choice block, so the page stays where it is.
  const changeTerm = (next: BillingTerm): void => {
    void navigate({
      search: { term: next },
      replace: true,
      resetScroll: false,
    });
  };
  return (
    <MarketingSuitePage
      account={account}
      onTermChange={changeTerm}
      term={term}
    />
  );
}
